import React, { useState } from 'react';
import FormInput from 'src/scripts/templates/_shared/ui-components/YogaUI/FormInput';
import { getCountryPostalConfig, getGeocode } from './storeLocatorUtils';
import { ShippingCountryState } from '../../../../utils/shipping';
import { observer } from 'mobx-react-lite';
import { LatLng, LatLngError, StoreLocatorType } from './storeLocatorTypes';
import {
  customerLocationState,
  setCustomerLatLongLocation,
  setCustomerZipcode,
  setCustomerLocationErrorState,
  setCustomerSddZipcode,
  resetCustomerLatLongLocation,
} from './customerLocationState';
import { getCurrentLocation } from './storeLocatorUtils';
import { bopisState } from '../Bopis/bopisState';
import { IVariant } from '../../types/PDP';
import { LoadingMoreSpinner } from '../../../product-listing-page/components/SmallUIComponents';
import { mapCountryCode } from '../FindInStore/findInStoresUtils';
import { reaction } from 'mobx';
import { useCartData } from '../../../../utils/cart';
import { sddState } from '../Sdd/sddState';
import { BopisAnalyticsData } from '../Bopis/bopisTypes';
import { fireBOPISDataLayerEvent } from 'src/scripts/utils/gtm-data-layer';

let StoreLocatorForm = ({
  storeLocatorType,
  selectedVariant,
  page = 'pdp',
  analyticsEventDetails,
}: {
  storeLocatorType?: StoreLocatorType;
  selectedVariant: IVariant | undefined;
  page?: 'pdp' | 'cart';
  analyticsEventDetails: BopisAnalyticsData;
}) => {
  const [resetKey, setResetKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const resetPostalInputValue = () => {
    setResetKey((prevKey: number) => prevKey + 1);
  };

  React.useEffect(
    () =>
      reaction(
        () => customerLocationState.zip,
        (zip) => {
          resetPostalInputValue();
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const cartData = useCartData({ watchForChanges: false });
  const { regex, maxLength, localizedPostalOrZipTitle } = getCountryPostalConfig();
  const label = `Search by ${localizedPostalOrZipTitle} code`;
  const inputErrorMessage = `Please enter a valid ${localizedPostalOrZipTitle} code.`;

  const postalCodeIds = {
    fis: 'postalCodeFindInStore',
    sdd: 'postalCodeSdd',
    bopis: 'postalCodeBopis',
  };
  const postalCodeId = postalCodeIds[storeLocatorType ?? 'bopis'];

  const onFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const postal = (formData.get(postalCodeId)?.toString() || '').replace(/\s+/g, ' ').trim().toUpperCase();
    const handleSddActions = () => {
      if (selectedVariant?.sku) sddState.fetchSddDataPdp(selectedVariant);
      if (page === 'cart') sddState.fetchSddDataCart(cartData);
    };

    try {
      const params = {
        postal_code: postal,
        country_code: mapCountryCode(ShippingCountryState.countryCode).toLowerCase(),
      };
      setCustomerLocationErrorState(false);
      if (!params.postal_code) return;

      // For BOPIS we don't need geocoding; set zip and trigger BOPIS fetches directly
      if (storeLocatorType === 'bopis') {
        resetCustomerLatLongLocation();
        setCustomerZipcode(postal);
        fireBOPISDataLayerEvent({
          ...analyticsEventDetails,
          postalCode: postal,
          eventName: 'bopis_postal_update_event',
        });

        if (selectedVariant?.sku) bopisState.fetchStoresDataPdp(selectedVariant);
        if (page === 'cart') bopisState.fetchStoresDataCart(cartData);
        return;
      }

      // For SDD / FIS we still rely on geocode
      const geocodeData: LatLng | LatLngError = await getGeocode(params);
      if ('latitude' in geocodeData && 'longitude' in geocodeData) {
        const { latitude, longitude } = geocodeData;
        if (latitude && longitude) {
          if (storeLocatorType === 'sdd') {
            setCustomerSddZipcode(postal);
            handleSddActions();
          } else {
            setCustomerZipcode(postal);
            setCustomerLatLongLocation(latitude.toString(), longitude.toString());
            fireBOPISDataLayerEvent({
              ...analyticsEventDetails,
              postalCode: postal,
              eventName: 'bopis_postal_update_event',
            });
          }
        }
      } else {
        // if error is 400, set zip code
        if (geocodeData.error.status === 400) {
          if (storeLocatorType === 'sdd') {
            setCustomerSddZipcode(postal);
            handleSddActions();
          } else {
            setCustomerZipcode(postal);
            fireBOPISDataLayerEvent({
              ...analyticsEventDetails,
              postalCode: postal,
              eventName: 'bopis_postal_update_event',
            });
          }
          // if error is 400, geocode api service is down set error state for FIS
          if (storeLocatorType === 'fis') setCustomerLocationErrorState(true);
        } else {
          // else, the error is 404 which means it's an invalid zip and should show user invalid zip error
          setCustomerLocationErrorState(true);
        }
        throw new Error('Latitude or Longitude is not defined.');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCurrentLocation = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    const result = await getCurrentLocation();
    fireBOPISDataLayerEvent({
      ...analyticsEventDetails,
      eventName: 'bopis_customer_location_retrieval_event',
    });
    if (result.success) {
      resetPostalInputValue();
    }
    setIsLoading(false);
  };

  return (
    <>
      <div className="store-locator-form-container d-flex">
        <form className="store-locator-form d-flex" onSubmit={onFormSubmit}>
          <FormInput
            key={resetKey} // Rerenders the input on coords change due to FormInput being uncontrolled React input. [REVISIT] Should FormInput be controlled for more flexibility???
            options={{
              type: 'text',
              id: postalCodeId,
              name: postalCodeId,
              errorText: inputErrorMessage,
              label,
              pattern: regex,
              defaultValue:
                storeLocatorType === 'sdd'
                  ? customerLocationState.sddZip ?? undefined
                  : customerLocationState.zip ?? undefined,
              maxLength: maxLength,
              required: true,
              showErrorOnBlur: false,
            }}
          />
          <button className="button-primary arrow-button" type="submit">
            <span className="alo-chevron-right-icon"></span>
          </button>
        </form>
        {isLoading ? (
          <div>
            <LoadingMoreSpinner />
          </div>
        ) : (
          storeLocatorType !== 'sdd' && (
            <div className="current-location-link d-flex">
              or
              <span className="current-location-link__button" onClick={handleCurrentLocation}>
                <span>Use my current location</span>
              </span>
            </div>
          )
        )}
      </div>
      {customerLocationState.apiError && (
        <p className="alert-text">That {localizedPostalOrZipTitle} code does not exist. Please try a different one.</p>
      )}
    </>
  );
};

StoreLocatorForm = observer(StoreLocatorForm);
export { StoreLocatorForm };
